import { HankoMenu } from '@/components/HankoMenu';
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';

/**
 * VaisalaHankoMenuElement — Custom Element wrapper for the HankoMenu React component.
 *
 * Renders the HankoMenu directly into the custom element (no Shadow DOM)
 * so that rockhopper styles work without isolation issues.
 *
 * Passes the `lang` attribute value as the `lang` prop to <HankoMenu />.
 * The event-based language switching is handled inside the React tree
 * via the shared `useLanguageSwitch` hook — no custom element-specific
 * listener needed.
 *
 * Passes the `menu-offset` attribute (parsed as a number) as the
 * `menuOffset` prop. One value, set once on the element — HankoMenu
 * fans it out to both the user menu and cart dropdown internally, so
 * they always move together.
 *
 * Portal scoping lives in the React tree (`usePortalScope`), so npm consumers
 * of <HankoMenu /> get it too — previously only this custom element did.
 *
 * On each connect: creates a fresh container + React root.
 * On disconnect: uses a macrotask delay (`setTimeout(fn, 0)`) before unmounting
 * to survive brief disconnect/reconnect cycles caused by DOM moves or React
 * reconciliation.
 *
 * The `icon-color` attribute sets the trigger icon color; when absent the
 * icons inherit the host's text color (or a host-set
 * `--hanko-menu-icon-color`).
 *
 * Observed attributes: lang, menu-offset, icon-color
 */
export class VaisalaHankoMenuElement extends HTMLElement {
  static readonly observedAttributes = ['lang', 'menu-offset', 'icon-color'];

  private reactRoot: Root | null = null;
  private container: HTMLDivElement | null = null;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;

  connectedCallback(): void {
    // If we were scheduled to unmount, cancel it — the element is back in the DOM
    if (this.disconnectTimer !== null) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
      // Re-attach existing container if it was removed
      if (this.container && !this.contains(this.container)) {
        this.appendChild(this.container);
      }
      // Re-render to pick up any attribute changes that occurred while disconnected
      this.render();
      return;
    }

    // Fresh connect — create container and root
    this.container = document.createElement('div');
    this.appendChild(this.container);
    this.reactRoot = createRoot(this.container);
    this.render();
  }

  disconnectedCallback(): void {
    // Delay teardown to survive brief disconnect/reconnect cycles (React
    // reconciliation, DOM moves). If connectedCallback fires before the
    // timer, the teardown is cancelled and the existing tree is reused.
    // Clear any previously scheduled timer to avoid stale callbacks.
    if (this.disconnectTimer !== null) {
      clearTimeout(this.disconnectTimer);
    }
    this.disconnectTimer = setTimeout(() => {
      this.disconnectTimer = null;
      this.teardown();
    }, 0);
  }

  private teardown(): void {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  attributeChangedCallback(
    _name: string,
    _oldValue: string | null,
    _newValue: string | null,
  ): void {
    if (this.isConnected && this.reactRoot) {
      this.render();
    }
  }

  private render(): void {
    if (!this.reactRoot) return;

    const lang = this.getAttribute('lang') || undefined;
    const menuOffset = this.parseMenuOffset();
    const iconColor = this.getAttribute('icon-color')?.trim() || undefined;

    this.reactRoot.render(
      React.createElement(HankoMenu, {
        lang,
        menuOffset,
        iconColor,
      }),
    );
  }

  /**
   * Parses the `menu-offset` attribute. Anything unusable yields `undefined`
   * so `HankoMenu`'s default takes over; an explicit `"0"` is a real zero gap.
   * Empty/whitespace is rejected before conversion because `Number('')` is `0`.
   */
  private parseMenuOffset(): number | undefined {
    const raw = this.getAttribute('menu-offset');
    if (raw === null) return undefined;
    const trimmed = raw.trim();
    if (trimmed === '') return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
